<template lang="pug">
  .b-task-tile-rename(v-if="rename")
    NameForm(
      field="Название задачи"
      error="Укажите название задачи"
      :value="task.name"
      @submit="updateTask('name', $event); rename = false"
      @abort="rename = false"
    )
  .b-task-tile(v-else :class="{ grey: task.isClosed }")
    ._head
      Link(:to="{ name: 'task-detail', params: { projectId: project.id, stageId: task.stage.id, taskId: task.id } }") {{ task.name }}
      BaseStatus._status(:status="task.status" :gray="task.isClosed" :editable="access.canUpdateTask()" @change="updateTask('status', $event)")
    ._priority
      BasePriority(:level="task.priority" :gray="task.isClosed" :editable="access.canUpdateTask()" @change="updateTask('priority', $event)")
    ._datetime(:class="dateFinishClass" @dragstart.stop.prevent draggable)
      div(v-if="task.dateFinish" @click="$refs.dp.open()") {{ dateFinishStr }}
      div(v-else @click="access.canUpdateTask() && $refs.dp.open()") Без<br>срока
      BaseDatepicker._datepicker(type="datetime" @input="dateChanged" :value="dateFinish" hidden ref="dp" @dragstart.native.prevent)
    UserUniversalTile._manager(:selected="task.manager" :editable="access.canUpdateTask(true)" :items="$store.getters['project/members']" @change="updateTask('manager', $event)" label="Нет исполнителя" emptyLabel="Нет участников проекта")
    EllipsisMenu._ellipsis(:items="ellipsis")
</template>

<script>
import modelTaskList from '@/model/taskList';

import utils from '@/utils/common';
import Access from '@/utils/access';

import UserUniversalTile from '@/components/UserUniversalTile.vue';
import BasePriority from '@/components/BasePriority.vue';
import BaseStatus from '@/components/BaseStatus.vue';
import BaseDatepicker from '@/components/BaseDatepicker.vue';
import EllipsisMenu from '@/components/EllipsisMenu.vue';
import NameForm from '@/components/base/NameForm.vue';

export default {
  name: 'TaskTile',
  components: { NameForm, UserUniversalTile, BaseStatus, BasePriority, BaseDatepicker, EllipsisMenu }, // eslint-disable-line
  props: {
    task: Object,
    project: Object,
  },
  data() {
    return {
      rename: false,
    };
  },
  computed: {
    access() { return new Access({ ...this.task, project: this.project }, this.$store.state.auth.user); },
    dateFinish() { return this.task.dateFinish ? new Date(this.task.dateFinish) : null; },
    dateFinishStr() { return utils.convertDateToString(new Date(this.task.dateFinish), 'short', true, false); },
    dateFinishClass() {
      if (this.task.isClosed) return '';
      if (!this.dateFinish) return '';

      const now = new Date();
      if (this.dateFinish < now) {
        return 'outdated';
      }

      if (this.dateFinish - now < 3 * 60 * 60 * 1000) {
        return 'burning';
      }

      if (this.dateFinish - now < 24 * 60 * 60 * 1000) {
        return 'hot';
      }

      return '';
    },
    ellipsis() {
      if (!this.access.canUpdateTask(true)) return [];

      return [
        { text: 'Переименовать',        icon: 'edit',   handler: () => { this.rename = true; } }, // eslint-disable-line
        { text: 'Редактировать',        icon: 'edit',   route: { name: 'task-edit', params: { stageId: this.task.stage.id, taskId: this.task.id } } }, // eslint-disable-line
        this.task.isClosed
          ? { text: 'Открыть',              icon: 'close',   handler: () => { this.updateTask('isClosed', false); } } // eslint-disable-line
          : { text: 'Закрыть',              icon: 'close',   handler: () => { this.updateTask('isClosed', true); } }, // eslint-disable-line
        { text: 'Удалить',              icon: 'trash', handler: () => { modelTaskList.remove(this.task) } }, // eslint-disable-line
      ];
    },
  },
  methods: {
    dateChanged(value) {
      this.updateTask('dateFinish', value);
    },
    updateTask(field, value) {
      const { id } = this.task;
      modelTaskList.update({ id, field, value });
    },
  },
};
</script>

<style scoped lang="scss">

.task-tile-rename {
  background: rgba(235, 239, 242, 0.5);
  border-radius: 6px;
  padding: 12px 24px;
}

.task-tile {
  background: rgba(235, 239, 242, 0.5);
  border-radius: 6px;
  padding: 12px 24px;
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr 40px 70px 230px 24px;
  align-items: center;

  &.grey {
    background: #e6ecf2;
  }

  &.grey &__head a {
    color: #c5c5c5;
    text-decoration: underline;
    text-underline-offset: -.4em;
    text-decoration-skip-ink: none;
    text-decoration-thickness: 2px;
  }

  &__head {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  &__title {
    transition: color 0.3s;
    &:hover {
      color: var(--color-hover);
    }
  }

  &__status {
    margin-bottom: 3px;
    width: fit-content;
  }

  &__priority {
    width: fit-content;
  }

  &__datetime {
    position: relative;
    text-align: center;
    font-size: 12px;
    line-height: 15px;
    justify-self: center;
    letter-spacing: 0.05em;
    color: var(--color-middle-gray);
    cursor: pointer;

    &.outdated {
      color: #EA4335;
    }

    &.burning {
      color: #FF8D4C;
    }

    &.hot {
      color: #F9D208;
    }
  }

  &__datepicker {
    position: absolute;
    cursor: default;
  }

  &__manager {
    &.grey {
      filter: grayscale(1);
      opacity: .7;
    }

    &::v-deep .name {
      font-size: 13px;
    }
  }

  @media (max-width: 992px) {
    grid-template-columns: 1fr 60px;

    &__head {
      grid-row: 1 / 2 span;
    }

    &__ellipsis {
      grid-row: 1;
      grid-column: 2;
      justify-self: end;
    }

    &__datetime,
    &__priority {
      grid-column: 2;
      justify-self: end;
    }

    &__manager {
      grid-row: 3;
    }
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr 1fr 1fr;

    &__ellipsis {
      grid-column: 3;
    }

    &__priority {
      grid-row: 1;
      grid-column: 1;
      justify-self: start;
    }

    &__datetime {
      justify-self: center;
    }

    &__head {
      grid-column: 1/ 3 span;
      grid-row: 2;
    }

    &__manager {
      grid-column: 1/ 3 span;
    }
  }
}
</style>
